'use client';

import Cookies from 'js-cookie';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getCurrentLang } from '@/lib/i18n/getCurrentLang';
import type { Expand } from '@/utils';
import { BENCHMARK_YEAR_TO_COMPARE } from '@/utils/constants';
import type { DateRange, SupportedLanguage } from '@/utils/globalTypes';

import type { DataMode, ProfileSettingsContextValue } from './types';

const ProfileSettingsContext = createContext<ProfileSettingsContextValue | null>(null);

export function ProfileSettingsProvider({
  children,
  bootstrapLang,
}: {
  children: React.ReactNode;
  bootstrapLang: SupportedLanguage;
}) {
  const [dataMode, _setDataMode] = useState<DataMode>('month');
  const [dateRangeMinMax, _setDateRangeMinMax] = useState<DateRange>({} as DateRange);
  const benchmarkYearToCompare = BENCHMARK_YEAR_TO_COMPARE;

  // Use a hydration-safe bootstrap value for the first render, then let runtime state own language changes.
  const [lang, _setLang] = useState<SupportedLanguage>(() => bootstrapLang);

  const setDataMode = useCallback((mode: DataMode) => _setDataMode(mode), []);
  const setDateRangeMinMax = useCallback((range: DateRange) => _setDateRangeMinMax(range), []);

  const setLang = useCallback((language: SupportedLanguage) => {
    // set cookies synchronously
    Cookies.set('lang', language, { expires: 365, path: '/' });
    _setLang(language);
  }, []);

  const value = useMemo<Expand<ProfileSettingsContextValue>>(
    () => ({ benchmarkYearToCompare, dataMode, dateRangeMinMax, lang, setDataMode, setDateRangeMinMax, setLang }),
    [benchmarkYearToCompare, dataMode, dateRangeMinMax, lang, setDataMode, setDateRangeMinMax, setLang]
  );

  useEffect(() => {
    const cookieLang = getCurrentLang();
    _setLang((currentLang) => (currentLang === cookieLang ? currentLang : cookieLang));
  }, []);

  // _document renders `<html lang>` from the build-time default, which is not
  // necessarily the language being read. Keep the attribute in step with the
  // active one: it picks the hyphenation dictionary for `hyphens: auto` and the
  // voice a screen reader announces the page with.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return <ProfileSettingsContext.Provider value={value}>{children}</ProfileSettingsContext.Provider>;
}

export function useProfileSettings() {
  const ctx = useContext(ProfileSettingsContext);
  if (!ctx) throw new Error('useProfileSettings must be used within a ProfileSettingsProvider');
  return ctx;
}
